export const LANDING_PAGE_TITLE = "Twap Detect - Screener algorithm";
export const SCREENER_PAGE_TITLE = "Twap Detect - Screener";
export const ACTIVE_ACCESS_TITLE_STORAGE_KEY = "twap:active-access-title:v1";

const ACTIVE_ACCESS_TITLE_FALLBACK_TTL_MS = 24 * 60 * 60 * 1000;

type ActiveAccessTitleHint = {
  user_id: string;
  expires_at_ms: number;
  current_period_end: string | null;
};

export function syncBrowserTitle(title: string) {
  document.title = title;
  ensurePropertyMeta("og:title").content = title;
  ensureMeta("twitter:title").content = title;
}

export function ensureMeta(name: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.name = name;
    document.head.appendChild(element);
  }
  return element;
}

export function ensurePropertyMeta(property: string) {
  let element = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute("property", property);
    document.head.appendChild(element);
  }
  return element;
}

export function activeAccessTitleExpiresAtMs(
  currentPeriodEnd: string | null | undefined,
  nowMs = Date.now(),
) {
  const periodEndMs = currentPeriodEnd ? Date.parse(currentPeriodEnd) : NaN;
  return Number.isFinite(periodEndMs) && periodEndMs > nowMs
    ? periodEndMs
    : nowMs + ACTIVE_ACCESS_TITLE_FALLBACK_TTL_MS;
}

export function rememberActiveAccessTitleHint(
  userId: string,
  currentPeriodEnd: string | null | undefined,
  nowMs = Date.now(),
) {
  try {
    const payload: ActiveAccessTitleHint = {
      user_id: userId,
      current_period_end: currentPeriodEnd ?? null,
      expires_at_ms: activeAccessTitleExpiresAtMs(currentPeriodEnd, nowMs),
    };
    window.localStorage.setItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Ignore private mode / quota errors. Runtime auth still sets the title.
  }
}

export function clearActiveAccessTitleHint(userId?: string | null) {
  try {
    if (!userId) {
      window.localStorage.removeItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY);
      return;
    }
    const raw = window.localStorage.getItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Partial<ActiveAccessTitleHint>;
    if (parsed.user_id === userId) {
      window.localStorage.removeItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY);
    }
  } catch {
    try {
      window.localStorage.removeItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }
}

export function hasActiveAccessTitleHint(userId: string | null | undefined, nowMs = Date.now()) {
  if (!userId) return false;
  try {
    const raw = window.localStorage.getItem(ACTIVE_ACCESS_TITLE_STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as Partial<ActiveAccessTitleHint>;
    return (
      parsed.user_id === userId &&
      typeof parsed.expires_at_ms === "number" &&
      parsed.expires_at_ms > nowMs
    );
  } catch {
    return false;
  }
}
